import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import * as actions from '../../actions/generalActions';
import * as warehouseActions from '../../actions/warehouse';
import { RootState } from '../../reducers';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { withRouter } from 'react-router';
import Spinner from '../../components/Spinner';
import WarehouseShipmentCard from './WarehouseShipmentCard';
import './warehouse.scss';
import './WarehouseShipmentCard.scss';

interface Props {
    history: any;
    intl: any;
    warehouse: any;
    warehouseActions: any;
    loading: boolean;
    actions: any;
    permissions: any;
}

const WarehouseShipmentForm: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage }),
        [intl]
    );

    const { clientId, projectId } = useParams<{ clientId?: string; projectId?: string }>();
    const [clientName, setClientName] = useState('');
    const [clientInfo, setClientInfo] = useState<any>(null);
    const [searchValue, setSearchValue] = useState('');
    const [projects, setProjects] = useState<any[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<any>(projectId);
    const [showAllProjects, setShowAllProjects] = useState(!projectId);

    // Load transport/installation users on mount
    useEffect(() => {
        props.warehouseActions.fetchTransportAssigneesOptions();
        props.warehouseActions.fetchInstallationAssigneesOptions();
    }, []);

    // Load client data when clientId changes
    useEffect(() => {
        if (clientId) {
            props.warehouseActions.fetchWarehouseClientInformation(clientId);
        }
    }, [clientId]);

    useEffect(() => {
        if (projectId) setSelectedProjectId(projectId);
    }, [projectId]);

    // Process client data
    useEffect(() => {
        const info = props.warehouse?.warehouseClientInformation;
        if (info && info.length > 0) {
            const client = info[0]?.client;
            if (client) {
                setClientName(client.name || '');
                setClientInfo(client);
            }
            setProjects(info);
        }
    }, [props.warehouse?.warehouseClientInformation]);

    const isProductAvailable = (prod: any) => prod.warehouseStatus === 'in_warehouse' && (prod.availableInWarehouse ?? 0) > 0;

    // All eligible projects (filtered by search + availability)
    const allEligibleProjects = useMemo(() => {
        return (searchValue
            ? projects.filter(p => p.projectName?.toLowerCase().includes(searchValue.toLowerCase()))
            : projects
        ).filter(p => {
            if (projectId && String(p.projectId) === String(projectId)) return true;
            if ((p.warehouseRecords || []).length > 0) return true;
            const prods = p.products || [];
            return prods.some((prod: any) => isProductAvailable(prod));
        }).sort((a, b) => {
            // Pin URL-selected project to top
            const aSelected = projectId && String(a.projectId) === String(projectId) ? 1 : 0;
            const bSelected = projectId && String(b.projectId) === String(projectId) ? 1 : 0;
            if (aSelected !== bSelected) return bSelected - aSelected;
            // Then sort by available product count
            const aCount = (a.products || []).filter((p: any) => isProductAvailable(p)).length;
            const bCount = (b.products || []).filter((p: any) => isProductAvailable(p)).length;
            return bCount - aCount;
        });
    }, [projects, searchValue, projectId]);

    // Focus mode: show only selected project, or all
    const filteredProjects = useMemo(() => {
        if (showAllProjects || !projectId) return allEligibleProjects;
        return allEligibleProjects.filter(p => String(p.projectId) === String(projectId));
    }, [allEligibleProjects, showAllProjects, projectId]);

    const otherProjectCount = allEligibleProjects.length - (projectId ? 1 : 0);

    // Stats for client card
    const totalProjectCount = projects.length;
    const totalAvailableProducts = useMemo(() => {
        return projects.reduce((sum, p) => {
            return sum + (p.products || []).filter((prod: any) => isProductAvailable(prod)).length;
        }, 0);
    }, [projects]);

    const handleBack = () => props.history.push('/dashboard/warehouse');

    const handleProjectClick = (pid: any) => {
        setSelectedProjectId(pid);
        props.history.push(`/dashboard/warehouse/create/${clientId}/${pid}`);
    };

    if (!clientId) {
        return (
            <div className="wsc-form-page">
                <div className="wsc-empty-state">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    <p><FormattedMessage id="warehouse.shipment.selectClient" defaultMessage="Zgjidhni klientin për të vazhduar" /></p>
                </div>
            </div>
        );
    }

    if (props.loading && !projects.length) return <Spinner />;

    return (
        <div className="wsc-form-page">
            {/* Client info card */}
            {clientInfo && (
                <div className="wsc-client-card">
                    <div className="wsc-client-avatar">
                        {(clientName || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="wsc-client-info">
                        <div className="wsc-client-name">{clientName}</div>
                        <div className="wsc-client-meta">
                            {[clientInfo.phoneNumber, clientInfo.city].filter(Boolean).join('  \u2022  ')}
                        </div>
                    </div>
                    <div className="wsc-client-stats">
                        <div className="wsc-client-stat">
                            <span className="wsc-client-stat-value">{filteredProjects.length}</span>
                            <span className="wsc-client-stat-label"><FormattedMessage id="warehouse.shipment.stat.projects" defaultMessage="Projekte" /></span>
                        </div>
                        <div className="wsc-client-stat-divider" />
                        <div className="wsc-client-stat">
                            <span className="wsc-client-stat-value">{totalAvailableProducts}</span>
                            <span className="wsc-client-stat-label"><FormattedMessage id="warehouse.shipment.stat.productsAvailable" defaultMessage="Produkte në depo" /></span>
                        </div>
                    </div>
                </div>
            )}

            {/* Toggle + search */}
            {projectId && otherProjectCount > 0 && (
                <div className="wsc-toggle-row">
                    <label className="wsc-switch-label" onClick={() => setShowAllProjects(!showAllProjects)}>
                        <span className={`wsc-switch ${showAllProjects ? 'wsc-switch-on' : ''}`}>
                            <span className="wsc-switch-thumb" />
                        </span>
                        <span className="wsc-switch-text">
                            <FormattedMessage id="warehouse.shipment.showAllProjects" defaultMessage="Të gjitha projektet" />
                            <span className="wsc-switch-count">{otherProjectCount} <FormattedMessage id="warehouse.shipment.others" defaultMessage="të tjera" /></span>
                        </span>
                    </label>
                    {showAllProjects && allEligibleProjects.length > 2 && (
                        <input
                            type="text"
                            className="wsc-search-input"
                            placeholder={fm('warehouse.shipment.searchProjects', 'Kërko projekte...')}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                    )}
                </div>
            )}
            {!projectId && allEligibleProjects.length > 2 && (
                <div className="wsc-toggle-row">
                    <input
                        type="text"
                        className="wsc-search-input"
                        placeholder={fm('warehouse.shipment.searchProjects', 'Kërko projekte...')}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                </div>
            )}

            {/* Empty state */}
            {projects.length === 0 && !props.loading && (
                <div className="wsc-empty-state">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>
                    <p><FormattedMessage id="warehouse.shipment.noWarehouseProducts" defaultMessage="Ky klient nuk ka produkte të gatshme në depo" /></p>
                </div>
            )}
            {projects.length > 0 && filteredProjects.length === 0 && (
                <div className="wsc-empty-state">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>
                    <p><FormattedMessage id="warehouse.shipment.noAvailable" defaultMessage="Asnjë produkt i disponueshëm për dërgesë" /></p>
                </div>
            )}

            {/* Project cards */}
            {filteredProjects.map((project, index) => {
                const isExpanded = selectedProjectId
                    ? String(project.projectId) === String(selectedProjectId)
                    : index === 0;
                return (
                    <WarehouseShipmentCard
                        key={project.projectId}
                        history={props.history}
                        warehouseProject={project}
                        projectId={project.projectId}
                        clientId={clientId}
                        clientName={clientName}
                        collapsed={!isExpanded}
                        projectIndex={index}
                        onExpand={() => handleProjectClick(project.projectId)}
                    />
                );
            })}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app, warehouse: state.warehouse };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        warehouseActions: bindActionCreators(warehouseActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(WarehouseShipmentForm as any)));
